import React, { RefObject, useEffect, useMemo, useRef } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { IChartingLibraryWidget, IPositionLineAdapter } from '../../charting_library';
import { useAccountEquity } from '../../hooks/useAccountEquity';
import { Order } from '../../hooks/useOrders';
import { Position } from '../../hooks/usePositions';
import { useProductPricesSub } from '../../hooks/useProductPricesSub';
import { ProductType } from '../../hooks/useProducts';
import { localStorageKeys } from '../../lib/localstorageKeys';
import { calcLiquidationPrice } from '../../lib/marginHelpers';
import { ChartPriceType, renderLiqPriceLine } from '../../lib/tradingViewHelpers';
import { tryCatch } from '../../lib/tryCatch';

type LiqPriceLineUpdaterProps = {
  product: ProductType;
  widgetRef: RefObject<IChartingLibraryWidget | null>;
  isChartReady: () => boolean;
  doesSymbolMatch: () => boolean;
  isSwitchingSymbol: boolean;
  position: Position | undefined;
  orders: Order[] | undefined;
  stopLossOrder: Order | undefined;
  takeProfitOrder: Order | undefined;
  priceDecimals: number;
  priceType: ChartPriceType;
};

/**
 * Separate the logic of the liquidation price line from the rest because
 * it depends on the price feed, which updates extremely frequently.
 */
export const LiqPriceLineUpdater: React.FC<LiqPriceLineUpdaterProps> = ({
  product,
  widgetRef,
  isChartReady,
  doesSymbolMatch,
  isSwitchingSymbol,
  position,
  orders,
  stopLossOrder,
  takeProfitOrder,
  priceDecimals,
  priceType,
}) => {
  const liqPriceLineRef = useRef<IPositionLineAdapter | undefined>(undefined);
  // Calculate the liquidation price
  const accountEquity = useAccountEquity();
  const { data: pricesByProductId } = useProductPricesSub();
  const [showLiquidationPrice] = useLocalStorage(localStorageKeys.showLiquidationPrice, true);

  const estLiqPrice = useMemo(() => {
    const oraclePrice = pricesByProductId?.[product.id]?.oraclePrice;
    if (!position || !accountEquity || !oraclePrice) return undefined;
    const clp = calcLiquidationPrice({
      accountEquity,
      positionSize: position.size,
      maintenanceMarginRatio: product.maintenanceMarginRatio,
      oraclePrice,
    });
    return clp;
  }, [pricesByProductId, product.id, product.maintenanceMarginRatio, position, accountEquity]);

  // Clean up liquidation price line when priceType changes
  useEffect(() => {
    return () => {
      if (liqPriceLineRef.current) {
        liqPriceLineRef.current.remove();
        liqPriceLineRef.current = undefined;
      }
    };
  }, [priceType]);

  useEffect(() => {
    // clean up the liqPrice line if we're switching symbols
    if (isSwitchingSymbol && liqPriceLineRef.current) {
      liqPriceLineRef.current.remove();
      liqPriceLineRef.current = undefined;
    }

    if (!isChartReady()) {
      return;
    }

    const chart = tryCatch(() => widgetRef.current?.activeChart());
    if (!doesSymbolMatch() || !chart) {
      return;
    }

    tryCatch(() =>
      renderLiqPriceLine(
        chart,
        orders || [],
        stopLossOrder,
        takeProfitOrder,
        position,
        estLiqPrice,
        liqPriceLineRef,
        priceDecimals,
        showLiquidationPrice
      )
    );
  }, [
    widgetRef,
    isChartReady,
    doesSymbolMatch,
    isSwitchingSymbol,
    priceDecimals,
    orders,
    stopLossOrder,
    takeProfitOrder,
    position,
    estLiqPrice,
    showLiquidationPrice,
  ]);

  return null;
};
